import React, { useEffect, useState } from "react";
import Box from "../../../components/Box";
import styled from "styled-components";
import Label from "../../../components/Label";
import CheckBoxDateRange from "./CheckBoxDateRange";
import LineChart from "../../../components/D3Charts/LineChart";
import { t } from "i18next";
import { Col, DatePicker, Flex, Row, Space } from "antd";
import Icon from "../../../components/Icon";
import { useSelector } from "react-redux";
import moment from "moment";
import DonationCheckbox from "./DonationCheckbox";
import { showNotification } from "../../../util/Utility";
import axios from "axios";
import { truncate } from "lodash";

const GRAPH_COLORS = ["#FC5C92", "#08C0CC", "#42AE99", "#4763E4", "#F5BC1A"];

const BoxContainer = styled(Box)`
  background: #f5f5f5;
  width: 160px;
  padding: 10px;
  border-radius: 8px;
  margin-left: 10px;
  height: 38px;
`;
const FinancialBoxShadow = styled.div`
  background: #ffffff;
  box-shadow: 0px 2px 8px rgba(40, 41, 61, 0.08),
    0px 20px 32px rgba(96, 97, 112, 0.24);
  border-radius: 30px;
  padding: 20px;
`;

const transformStatsForChart = (stats, donationsList) => {
  const dataSet = [];
  const colors = [];

  Object.entries(stats).forEach(([campaignId, monthlyStats], index) => {
    const monthlyAmounts = Array(12).fill(0); // 👈 fill with null, not 0

    monthlyStats.forEach((item) => {
      const monthIndex = item.month - 1;
      monthlyAmounts[monthIndex] = item.totalAmount ?? 0;
    });

    const campaign = donationsList.find((d) => d.value === campaignId);
    const name = campaign?.label || `Campaign ${index + 1}`;

    dataSet.push({ name, data: monthlyAmounts });
    colors.push(GRAPH_COLORS[index % GRAPH_COLORS.length]);
  });

  return { dataSet, legends: dataSet.map((d) => d.name), colors };
};

const FundsGraph = ({ title, dateRange, handleDateRange }) => {
  const [year, setYear] = useState();
  const [donationsList, setDonationsList] = useState([]);
  const [selectedDonations, setSelectedDonations] = useState([]);
  const [dataSet, setDataSet] = useState([]);
  const [legends, setLegends] = useState([]);
  const [colors, setColors] = useState([]);

  const activeEntity = useSelector((state) => state.adminReducer.activeEntity);
  const userType = useSelector((state) => state?.appReducer?.userType);

  const disabledYears = (current) => {
    const date = new Date(activeEntity.created_at);
    const minYear = moment(date).startOf("year");
    const maxYear = moment().endOf("year");
    return current && (current < minYear || current > maxYear);
  };

  // 🔄 Fetch available donations
  useEffect(() => {
    const fetchDonations = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}donation/formatted-list?entity_id=${activeEntity?._id}`
        );
        if (res.status === 200 && res.data?.donations) {
          const formattedList = res.data.donations.map((item) => ({
            label: truncate(item.title, { length: 30 }),
            value: item._id,
          }));
          setDonationsList(formattedList);
        }
      } catch (error) {
        console.log("Donations error:", error);
        showNotification("error", "Error fetching donations");
      }
    };
    fetchDonations();
  }, [activeEntity]);

  // 📊 Fetch donation payment stats
  const fetchDonationStats = async (isClearAll = false) => {
    try {
      if (!year && !isClearAll) {
        showNotification("warning", "Please select Donations year");
        return;
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}donation/donation-payments-stats`,
        {
          donationIds: isClearAll ? [] : selectedDonations,
          year,
        }
      );

      if (res.status === 200 && res.data?.stats) {
      
        const { dataSet, legends, colors } = transformStatsForChart(
          res.data.stats,
          donationsList
        );
        setDataSet(dataSet);
        setLegends(legends);
        setColors(colors);
      }
    } catch (error) {
      console.log("DONATION STATS ERROR: ", error);
      showNotification("error", "Donation stats not fetched");
    }
  };

  // 🔄 Fetch stats on selection
  useEffect(() => {
    if (year) {
      fetchDonationStats(false);
    }
  }, [year]);

  return (
    <Box width="100%" maxWidth="100%" marginTop="30px">
      <FinancialBoxShadow>
        <Flex
          className="pb-md-4 pb-2 px-3"
          gap="middle"
          wrap
          align="center"
          justify="space-between"
          horizontal
        >
          <Label fontSize="24px" fontWeight="700" justifyContent="center">
            {t(`Total Donations Income`)}
          </Label>

          {userType === "admin" ? (
            <div
              style={{
                display: "flex",
                gap: "20px",
                justifyContent: "center",
                width: "max-content",
                alignItems: "center",
              }}
            >
              <DonationCheckbox
                options={donationsList}
                onChange={(selected) => setSelectedDonations(selected)}
                label="Select Campaign"
                fetchDonationStats={fetchDonationStats}
                selectedDonations={selectedDonations}
              />
              <div className="col" style={{ width: "170px" }}>
                <label
                  htmlFor="Datum"
                  style={{
                    display: "block",
                    fontWeight: 500,
                    color: "#7E7E7E",
                    fontSize: "14px",
                  }}
                >
                  {t("Select Year")}
                  <span style={{ color: "#FE1717" }}>*</span>
                </label>
                <DatePicker
                  className="input-box calendar"
                  placeholder={t("year")}
                  style={{ height: "40px" }}
                  onChange={(e) => setYear(e.$y)}
                  disabledDate={disabledYears}
                  picker="year"
                  suffixIcon={<Icon iconName="calendar" />}
                />
              </div>
            </div>
          ) : (
            dateRange && (
              <label>
                <div style={{ flex: "right" }}>
                  <BoxContainer>
                    <CheckBoxDateRange
                      value={dateRange}
                      handleSortBy={handleDateRange}
                    />
                  </BoxContainer>
                </div>
              </label>
            )
          )}
        </Flex>

        <LineChart
          dataSet={dataSet}
          colors={colors}
          categories={[
            "Jan",
            "Feb",
            "Mrt",
            "Apr",
            "Mei",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Okt",
            "Nov",
            "Dec",
          ]}
          xLabel="Month"
          yLabel="Amount"
          legends={legends}
          yAxisLabelFormat={(value) => value.toFixed(2)}
        />
      </FinancialBoxShadow>
    </Box>
  );
};

export default FundsGraph;
